import { useState, useCallback, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload as UploadIcon, Music, Film, Tv, X, Check, AlertCircle, FileAudio, FileVideo, Settings, Loader2, User, Disc3, Clock, Headphones } from 'lucide-react'
import { Link } from 'react-router-dom'
import { upload, type AudioPreviewResponse } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type { Library, Track, Video } from '../types'

type LibraryType = 'music' | 'movies' | 'tv'

interface UploadFile {
  file: File
  id: string
  progress: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
  result?: Track | Track[] | Video
  preview?: AudioPreviewResponse
  previewLoading?: boolean
  previewError?: string
}

// Format duration in mm:ss
const formatDuration = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Format file size
const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

// Format bitrate
const formatBitrate = (kbps: number) => {
  if (kbps >= 1000) {
    return `${(kbps / 1000).toFixed(1)} Mbps`
  }
  return `${kbps} kbps`
}

// Format sample rate
const formatSampleRate = (hz: number) => {
  return `${(hz / 1000).toFixed(1)} kHz`
}

const libraryTypeConfig = {
  music: {
    icon: Music,
    label: 'Music',
    accept: '.mp3,.flac,.m4a,.aac,.ogg,.wav,.opus,.wma',
    description: 'MP3, FLAC, M4A, AAC, OGG, WAV, OPUS, WMA',
  },
  movies: {
    icon: Film,
    label: 'Movies',
    accept: '.mp4,.mkv,.avi,.mov,.wmv,.webm',
    description: 'MP4, MKV, AVI, MOV, WMV, WEBM',
  },
  tv: {
    icon: Tv,
    label: 'TV Shows',
    accept: '.mp4,.mkv,.avi,.mov,.wmv,.webm',
    description: 'MP4, MKV, AVI, MOV, WMV, WEBM',
  },
}

export default function Upload() {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const [libraryType, setLibraryType] = useState<LibraryType>('music')
  const [files, setFiles] = useState<UploadFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadingRef = useRef(false)

  // Get or create the upload library for the selected type
  const { data: uploadLibrary, isLoading: libraryLoading, error: libraryError } = useQuery({
    queryKey: ['upload-library', libraryType],
    queryFn: () => upload.getUploadLibrary(libraryType),
  })

  const config = libraryTypeConfig[libraryType]
  const Icon = config.icon

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles)
    const uploadFiles: UploadFile[] = fileArray.map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      progress: 0,
      status: 'pending',
      previewLoading: libraryType === 'music' && !file.name.toLowerCase().endsWith('.zip'),
    }))
    setFiles((prev) => [...prev, ...uploadFiles])

    // Fetch metadata preview for music files
    if (libraryType === 'music') {
      uploadFiles.forEach(async (uploadFile) => {
        try {
          const preview = await upload.previewTrack(uploadFile.file)
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadFile.id
                ? { ...f, preview, previewLoading: false }
                : f
            )
          )
        } catch (error: any) {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadFile.id
                ? { ...f, previewLoading: false, previewError: error.message || 'Failed to preview' }
                : f
            )
          )
        }
      })
    }
  }, [libraryType])

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }, [])

  const uploadFile = useCallback(async (uploadFile: UploadFile, library: Library) => {
    const isAudio = libraryType === 'music'

    setFiles((prev) =>
      prev.map((f) =>
        f.id === uploadFile.id ? { ...f, status: 'uploading' } : f
      )
    )

    try {
      const result = isAudio
        ? await upload.uploadTrack(library.id, uploadFile.file, (progress) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === uploadFile.id ? { ...f, progress } : f
              )
            )
          })
        : await upload.uploadVideo(library.id, uploadFile.file, (progress) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === uploadFile.id ? { ...f, progress } : f
              )
            )
          })

      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? { ...f, status: 'success', progress: 100, result }
            : f
        )
      )

      // Invalidate relevant queries
      if (isAudio) {
        queryClient.invalidateQueries({ queryKey: ['tracks'] })
        queryClient.invalidateQueries({ queryKey: ['albums'] })
        queryClient.invalidateQueries({ queryKey: ['artists'] })
      } else {
        queryClient.invalidateQueries({ queryKey: ['movies'] })
        queryClient.invalidateQueries({ queryKey: ['series'] })
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed'
      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id
            ? { ...f, status: 'error', error: errorMessage }
            : f
        )
      )
    }
  }, [libraryType, queryClient])

  const startUploads = useCallback(async () => {
    if (!uploadLibrary || uploadingRef.current) return

    uploadingRef.current = true
    const pendingFiles = files.filter((f) => f.status === 'pending')

    for (const file of pendingFiles) {
      await uploadFile(file, uploadLibrary)
    }

    uploadingRef.current = false
  }, [files, uploadLibrary, uploadFile])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }, [addFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files)
      e.target.value = '' // Reset input
    }
  }, [addFiles])

  const clearCompleted = useCallback(() => {
    setFiles((prev) => prev.filter((f) => f.status !== 'success'))
  }, [])

  const pendingCount = files.filter((f) => f.status === 'pending').length
  const uploadingCount = files.filter((f) => f.status === 'uploading').length
  const completedCount = files.filter((f) => f.status === 'success').length
  const errorCount = files.filter((f) => f.status === 'error').length

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">Upload Media</h1>

      {/* Library Type Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-dark-300 mb-2">
          Select Media Type
        </label>
        <div className="flex gap-2 flex-wrap">
          {(Object.keys(libraryTypeConfig) as LibraryType[]).map((type) => {
            const TypeIcon = libraryTypeConfig[type].icon
            const isActive = libraryType === type
            return (
              <button
                key={type}
                onClick={() => {
                  setLibraryType(type)
                  setFiles([]) // Clear files when changing type
                }}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                <TypeIcon size={20} />
                <span>{libraryTypeConfig[type].label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Upload Library Status */}
      {libraryLoading && (
        <div className="mb-6 p-4 bg-dark-800 rounded-lg text-dark-400">
          Loading upload library...
        </div>
      )}

      {libraryError && (
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/50 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-medium">No upload target configured</p>
              <p className="text-dark-400 text-sm mt-1">
                {user?.is_admin ? (
                  <>
                    Set a global library as the upload target for {libraryType} files.{' '}
                    <Link to="/libraries" className="text-primary-400 hover:text-primary-300 inline-flex items-center gap-1">
                      <Settings size={14} />
                      Configure in Libraries
                    </Link>
                  </>
                ) : (
                  'An administrator must configure a global library as the upload target for this media type.'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {uploadLibrary && (
        <div className="mb-6 p-4 bg-dark-800 rounded-lg">
          <p className="text-sm text-dark-400">
            Uploading to global catalog: <span className="text-dark-200">{uploadLibrary.name}</span>
          </p>
          <p className="text-xs text-dark-500 mt-1">
            Files will be organized by Artist/Album and available to all users
          </p>
        </div>
      )}

      {/* Dropzone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-8 sm:p-12 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-primary-500 bg-primary-500/10'
            : 'border-dark-700 hover:border-dark-500 bg-dark-800/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={config.accept}
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            isDragging ? 'bg-primary-600' : 'bg-dark-700'
          }`}>
            <UploadIcon size={32} className={isDragging ? 'text-white' : 'text-dark-400'} />
          </div>

          <div>
            <p className="text-lg font-medium text-dark-200">
              {isDragging ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-dark-500 mt-1">
              or click to browse
            </p>
          </div>

          <p className="text-xs text-dark-500">
            Supported formats: {config.description}
          </p>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              Files ({files.length})
            </h2>
            <div className="flex gap-2">
              {completedCount > 0 && (
                <button
                  onClick={clearCompleted}
                  className="btn btn-secondary text-sm"
                >
                  Clear Completed
                </button>
              )}
              {pendingCount > 0 && uploadLibrary && (
                <button
                  onClick={startUploads}
                  disabled={uploadingCount > 0}
                  className="btn btn-primary text-sm"
                >
                  {uploadingCount > 0 ? 'Uploading...' : `Upload ${pendingCount} File${pendingCount !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mb-4 text-sm">
            {pendingCount > 0 && (
              <span className="text-dark-400">{pendingCount} pending</span>
            )}
            {uploadingCount > 0 && (
              <span className="text-blue-400">{uploadingCount} uploading</span>
            )}
            {completedCount > 0 && (
              <span className="text-green-400">{completedCount} completed</span>
            )}
            {errorCount > 0 && (
              <span className="text-red-400">{errorCount} failed</span>
            )}
          </div>

          {/* File items */}
          <div className="space-y-2">
            {files.map((uploadFile) => (
              <div
                key={uploadFile.id}
                className="p-3 bg-dark-800 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {libraryType === 'music' ? (
                      <FileAudio size={24} className="text-dark-400" />
                    ) : (
                      <FileVideo size={24} className="text-dark-400" />
                    )}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    {/* Title/Filename row */}
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {uploadFile.preview?.title || uploadFile.file.name}
                      </p>
                      {uploadFile.previewLoading && (
                        <Loader2 size={14} className="animate-spin text-primary-400 flex-shrink-0" />
                      )}
                    </div>

                    {/* Music metadata preview */}
                    {libraryType === 'music' && uploadFile.preview && (
                      <div className="mt-1 space-y-1">
                        {/* Artist and Album */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-dark-400">
                          {uploadFile.preview.artist && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {uploadFile.preview.artist}
                            </span>
                          )}
                          {uploadFile.preview.album && (
                            <span className="flex items-center gap-1">
                              <Disc3 size={12} />
                              {uploadFile.preview.album}
                            </span>
                          )}
                          {uploadFile.preview.year && (
                            <span>({uploadFile.preview.year})</span>
                          )}
                        </div>

                        {/* Technical details */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-dark-500">
                          <span className="font-medium text-primary-400">
                            {uploadFile.preview.file_format}
                          </span>
                          {uploadFile.preview.duration_ms && (
                            <span className="flex items-center gap-1">
                              <Clock size={11} />
                              {formatDuration(uploadFile.preview.duration_ms)}
                            </span>
                          )}
                          {uploadFile.preview.bitrate && (
                            <span className="flex items-center gap-1">
                              <Headphones size={11} />
                              {formatBitrate(uploadFile.preview.bitrate)}
                            </span>
                          )}
                          {uploadFile.preview.sample_rate && (
                            <span>{formatSampleRate(uploadFile.preview.sample_rate)}</span>
                          )}
                          <span>{formatFileSize(uploadFile.preview.file_size)}</span>
                        </div>
                      </div>
                    )}

                    {/* Fallback for non-music or no preview */}
                    {(libraryType !== 'music' || (!uploadFile.preview && !uploadFile.previewLoading)) && (
                      <p className="text-xs text-dark-500">
                        {formatFileSize(uploadFile.file.size)}
                      </p>
                    )}

                    {/* Preview error */}
                    {uploadFile.previewError && (
                      <p className="text-xs text-yellow-400 mt-1">
                        Metadata preview unavailable
                      </p>
                    )}

                    {/* Progress bar */}
                    {uploadFile.status === 'uploading' && (
                      <div className="mt-2 h-1.5 bg-dark-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-600 transition-all duration-300"
                          style={{ width: `${uploadFile.progress}%` }}
                        />
                      </div>
                    )}

                    {/* Error message */}
                    {uploadFile.status === 'error' && uploadFile.error && (
                      <p className="text-xs text-red-400 mt-1">{uploadFile.error}</p>
                    )}

                    {/* Success message */}
                    {uploadFile.status === 'success' && uploadFile.result && (
                      <p className="text-xs text-green-400 mt-1">
                        Uploaded as: {Array.isArray(uploadFile.result) ? `${uploadFile.result.length} tracks` : uploadFile.result.title}
                      </p>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div className="flex-shrink-0">
                    {uploadFile.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removeFile(uploadFile.id)
                        }}
                        className="p-1.5 text-dark-400 hover:text-dark-200 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    )}
                    {uploadFile.status === 'uploading' && (
                      <span className="text-sm text-blue-400">{uploadFile.progress}%</span>
                    )}
                    {uploadFile.status === 'success' && (
                      <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Check size={14} className="text-green-400" />
                      </div>
                    )}
                    {uploadFile.status === 'error' && (
                      <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                        <AlertCircle size={14} className="text-red-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {files.length === 0 && !libraryLoading && uploadLibrary && (
        <div className="mt-8 text-center text-dark-500">
          <Icon size={48} className="mx-auto mb-4 opacity-50" />
          <p>No files selected</p>
          <p className="text-sm mt-1">
            Add some {libraryType === 'music' ? 'audio' : 'video'} files to get started
          </p>
        </div>
      )}
    </div>
  )
}
